import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  ArrowsRightLeftIcon,
  CircleStackIcon,
  DocumentIcon
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import i18n from '../i18n';
import { useAuth } from '../context/AuthContext';

const API_URL = '/api/v1';

export default function Transfers() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const response = await axios.get(`${API_URL}/transfers`);
        setHistory(response.data);
      } catch (error) {
        console.error('Error fetching transfers:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, []);

  const totalExported = history.reduce((acc, t) => acc + Number(t.amount_mwh || 0), 0);

  const handleExportXML = async (e, transferId) => {
    e.stopPropagation();
    try {
      const response = await axios.get(`/api/v1/reporting/export/xml/transfer/${transferId}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `transfer_${transferId.substring(0, 8)}.xml`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Error exporting XML:', err);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-40">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#155e75]" />
    </div>
  );

  return (
    <div className="space-y-6 sm:space-y-8 animate-fade-in max-w-6xl mx-auto py-4 sm:py-6 px-2 sm:px-4 md:px-0">

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-slate-100 pb-6">
        <div className="text-center sm:text-left">
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
            {i18n.t('transfers_ledger')}
          </h1>
          <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">
            {i18n.t('trading_limit')} — 12 {i18n.t('months')} (RED II)
          </p>
        </div>

        <button
          onClick={() => navigate('/transfers/new')}
          className="w-full sm:w-auto bg-[#155e75] hover:bg-[#0e4e61] text-white px-6 py-3.5 rounded-xl text-xs font-bold uppercase tracking-wider shadow-lg shadow-[#155e75]/10 hover:shadow-xl transition-all flex items-center justify-center gap-2.5"
        >
          <ArrowsRightLeftIcon className="w-4 h-4" />
          {i18n.t('start_transfer')}
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
        <div className="bg-white p-6 rounded-2xl border border-[#adc6ce]/30 shadow-sm flex items-center gap-5">
          <div className="p-3.5 bg-blue-50 rounded-xl text-blue-600 flex-shrink-0">
            <CircleStackIcon className="w-8 h-8" />
          </div>
          <div>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">
              {i18n.t('market_liquidity')}
            </p>
            <p className="text-xl sm:text-2xl font-bold text-slate-800 tracking-tight">
              {totalExported.toLocaleString()} <span className="text-xs font-medium text-slate-400">MWh</span>
            </p>
          </div>
        </div>

        <div className="bg-white p-6 rounded-2xl border border-[#adc6ce]/30 shadow-sm flex items-center gap-5">
          <div className="p-3.5 bg-[#f6f6f6] rounded-xl text-[#155e75] flex-shrink-0">
            <ArrowsRightLeftIcon className="w-8 h-8" />
          </div>
          <div>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">
              {i18n.t('trading_limit')}
            </p>
            <p className="text-xl sm:text-2xl font-bold text-slate-800 tracking-tight">
              {history.length} <span className="text-xs font-medium text-slate-400">{i18n.t('transactions')}</span>
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white border border-[#adc6ce]/30 rounded-2xl overflow-hidden shadow-sm mt-6">

        <div className="hidden md:block overflow-x-auto">
          <table className="ent-table w-full text-left text-xs">
            <thead>
              <tr className="bg-slate-50/50 text-slate-400 font-bold uppercase tracking-wider border-b border-slate-100">
                <th className="py-4 px-5">{i18n.t('transaction_id')}</th>
                <th className="py-4">{i18n.t('recipient_entity')}</th>
                <th className="py-4">{i18n.t('domain_col')}</th>
                <th className="py-4">{i18n.t('volume_col')} (MWh)</th>
                <th className="py-4">{i18n.t('issuance_col')}</th>
                <th className="py-4 text-right px-5">{i18n.t('eecs_hub_status')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {history.map(t => (
                <tr key={t.transfer_id} className="hover:bg-slate-50/30 transition-colors">
                  <td className="py-4 px-5 font-mono text-[10px] text-slate-400 font-bold">
                    {t.transfer_id.substring(0, 12).toUpperCase()}
                  </td>
                  <td className="py-4 font-bold text-slate-800 uppercase tracking-tight">
                    {t.to_holder_name}
                  </td>
                  <td className="py-4 text-[#155e75] font-bold text-[10px]">
                    {t.to_registry}
                  </td>
                  <td className="py-4 font-extrabold text-slate-900">
                    {t.amount_mwh.toLocaleString()} <span className="text-[9px] text-slate-400">MWh</span>
                  </td>
                  <td className="py-4 text-slate-400 font-semibold">
                    {new Date(t.transfer_date).toLocaleDateString()}
                  </td>
                  <td className="py-4 text-right px-5 flex items-center justify-end gap-3">
                    <button
                      onClick={(e) => handleExportXML(e, t.transfer_id)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 border border-slate-200 text-[10px] font-bold uppercase text-slate-600 rounded-lg hover:bg-[#155e75] hover:text-white transition-all shadow-sm"
                    >
                      <DocumentIcon className="w-3.5 h-3.5" /> XML
                    </button>
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[9px] font-bold border ${t.transfer_status === 'COMPLETED' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-amber-50 text-amber-700 border-amber-100'}`}>
                      {t.transfer_status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="block md:hidden divide-y divide-slate-100">
          {history.map(t => (
            <div key={t.transfer_id} className="p-5 space-y-4">
              <div className="flex justify-between items-start gap-2">
                <div>
                  <span className="text-slate-400 block text-[9px] uppercase font-bold tracking-wider">Transaction ID</span>
                  <span className="font-mono text-xs font-bold text-slate-600 bg-slate-100 px-2 py-0.5 rounded">
                    {t.transfer_id.substring(0, 12).toUpperCase()}
                  </span>
                </div>
                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[9px] font-bold border ${t.transfer_status === 'COMPLETED' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                  {t.transfer_status}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-y-3 gap-x-2 text-[11px]">
                <div>
                  <span className="text-slate-400 block text-[9px] uppercase font-bold tracking-wider">Recipient</span>
                  <span className="text-slate-800 font-bold block truncate">{t.to_holder_name}</span>
                </div>
                <div>
                  <span className="text-slate-400 block text-[9px] uppercase font-bold tracking-wider">Domain</span>
                  <span className="text-[#155e75] font-bold block">{t.to_registry}</span>
                </div>
                <div>
                  <span className="text-slate-400 block text-[9px] uppercase font-bold tracking-wider">Volume</span>
                  <span className="text-slate-900 font-extrabold block">
                    {t.amount_mwh.toLocaleString()} <span className="text-[9px] text-slate-400 font-normal">MWh</span>
                  </span>
                </div>
                <div>
                  <span className="text-slate-400 block text-[9px] uppercase font-bold tracking-wider">Date</span>
                  <span className="text-slate-500 font-semibold block">{new Date(t.transfer_date).toLocaleDateString()}</span>
                </div>
              </div>

              <div className="pt-2 border-t border-slate-50 flex justify-end">
                <button
                  onClick={(e) => handleExportXML(e, t.transfer_id)}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 py-2 px-4 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold uppercase text-slate-600 active:bg-[#155e75] active:text-white transition-all"
                >
                  <DocumentIcon className="w-4 h-4" /> Download XML Report
                </button>
              </div>
            </div>
          ))}
        </div>

        {history.length === 0 && (
          <div className="p-16 text-center flex flex-col items-center justify-center text-slate-400 uppercase tracking-widest font-bold text-xs">
            <ArrowsRightLeftIcon className="w-12 h-12 text-slate-200 mb-4" />
            {i18n.t('no_transactions')}
          </div>
        )}

      </div>
    </div>
  );
}